import IntlService from 'ember-intl/services/intl';
import { ref } from 'ember-ref-bucket';
import { closest } from 'volta/utils/dom-utils';
import { getOrElse } from 'volta/utils/object-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { isEmpty } from '@ember/utils';

interface IArgs {
  initiallyEditing?: boolean;
  editModeOnly?: boolean;
  disabled: boolean;
  canEndEditing?: boolean;
  onEditingStart?: () => void;
  onEditingEnd?: () => void;
  onSubmit?: () => void;
}

export default class BaseFormEditor extends Component<IArgs> {
  @service intl!: IntlService;
  @ref('formRef') formRef?: HTMLElement | null;

  @tracked isEditing: boolean = Boolean(
    getOrElse(this.args.editModeOnly, this.args.initiallyEditing ?? false)
  );

  get canEndEditing() {
    return this.args.canEndEditing ?? true;
  }

  @action
  startEditing() {
    if (
      this.isEditing ||
      this.args.disabled ||
      (this.args.editModeOnly && isEmpty(this.args.initiallyEditing))
    ) {
      return;
    }
    this.isEditing = true;
    this.args.onEditingStart?.();
  }

  @action
  endEditing(event: Event) {
    const closestDropdown = closest(
      event.target as HTMLElement,
      '.ember-basic-dropdown-content,.bv-backdrop'
    );
    const closestElement = closest(
      this.formRef as HTMLElement,
      '.ember-basic-dropdown-content,.bv-backdrop'
    );

    const closestIsChild = closestDropdown?.id
      ? Array.from(document.querySelectorAll(`[aria-owns=${closestDropdown.id}]`))
      : [];

    let found = false;

    if (this.formRef) {
      for (const e of closestIsChild) {
        if (this.formRef.contains(e)) {
          found = true;
          break;
        }
      }
    }

    if (
      (closestDropdown && closestElement && closestDropdown !== closestElement) ||
      !this.isEditing ||
      this.args.disabled ||
      (this.args.editModeOnly && isEmpty(this.args.initiallyEditing))||
      !this.canEndEditing ||
      found
    ) {
      return;
    }
    this.isEditing = false;
    this.args.onEditingEnd?.();
  }

  @action
  submit(event: Event) {
    if (this.args.disabled || this.args.editModeOnly) {
      return;
    }
    this.args.onSubmit?.();
    this.endEditing(event);
  }

  toggleEditing = (event: Event) => {
    if (this.args.disabled || this.args.editModeOnly) {
      return;
    }
    if (this.isEditing) {
      this.endEditing(event);
    } else {
      this.startEditing();
    }
  };
}
